import {
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SPACING } from "../../constants/spacing";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import Button from "../Button";

function SubscriptionSettings() {
  const { t } = useTranslation("settings");
  const { subscriptionPlan } = useAuth();
  const currentPlan = subscriptionPlan || "free";

  const planDetails = {
    free: {
      name: t("subscriptionSettings.plans.free.name"),
      description: t("subscriptionSettings.plans.free.description"),
      color: "default",
      features: t("subscriptionSettings.plans.free.features", {
        returnObjects: true,
      }),
    },
    basic: {
      name: t("subscriptionSettings.plans.basic.name"),
      description: t("subscriptionSettings.plans.basic.description"),
      color: "secondary",
      features: t("subscriptionSettings.plans.basic.features", {
        returnObjects: true,
      }),
    },
    premium: {
      name: t("subscriptionSettings.plans.premium.name"),
      description: t("subscriptionSettings.plans.premium.description"),
      color: "primary",
      features: t("subscriptionSettings.plans.premium.features", {
        returnObjects: true,
      }),
    },
  };

  const currentPlanDetails = planDetails[currentPlan] || planDetails.free;

  const handleManagePlans = () => {
    window.location.href = "/account/membership";
  };

  const { colors } = useTheme();

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: SPACING.sm }}>
        {t("subscriptionSettings.title")}
      </Typography>

      <Typography
        variant="bodyMd"
        component="p"
        sx={{ color: "text.secondary", pb: SPACING.lg }}
      >
        {t("subscriptionSettings.description")}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: SPACING.sm }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("subscriptionSettings.currentPlan", {
                  plan: currentPlanDetails.name,
                })}
              </Typography>
              <Typography variant="bodySm" color="text.secondary">
                {currentPlanDetails.description}
              </Typography>
            </Box>
            <Chip
              label={currentPlanDetails.name}
              color={currentPlanDetails.color}
              sx={{ fontWeight: 500 }}
            />
          </Box>

          <Typography
            variant="subtitle2"
            sx={{ marginTop: SPACING.md, mb: SPACING.xs, fontWeight: 500 }}
          >
            {t("subscriptionSettings.whatsIncluded")}
          </Typography>

          <List sx={{ p: 0, mb: SPACING.sm }}>
            {currentPlanDetails.features.map((feature, index) => (
              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Check size={16} color={colors.success} />
                </ListItemIcon>
                <ListItemText
                  primary={<Typography variant="bodySm">{feature}</Typography>}
                />
              </ListItem>
            ))}
          </List>

          <Button
            variant="contained"
            endIcon={<ArrowRight size={16} />}
            onClick={handleManagePlans}
            sx={{ mt: 1 }}
          >
            {currentPlan === "premium"
              ? t("subscriptionSettings.managePlan")
              : t("subscriptionSettings.upgradePlan")}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
            {t("subscriptionSettings.planComparison.title")}
          </Typography>
          <Typography variant="bodySm" color="text.secondary">
            {t("subscriptionSettings.planComparison.description")}
          </Typography>
          <Button
            sx={{ mt: SPACING.sm }}
            variant="secondary"
            onClick={handleManagePlans}
            endIcon={<ArrowRight size={16} />}
          >
            {t("subscriptionSettings.viewAllPlans")}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SubscriptionSettings;
